<template>
  <div class="flex-child">
    <div class="body-container" style="min-width: 90vw; min-height: 80vh;">
      <header>
        <h2>Knowledge Configuration</h2>
        <div class="header-actions">
            <div class="genesys-status">
              <span class="status-indicator" :class="genesysStatus.connected ? 'connected' : 'disconnected'">
                {{ genesysStatus.connected ? '🟢 Genesys Connected' : '🔴 Genesys Disconnected' }}
              </span>
            </div>
        </div>
      </header>
      <div style="padding-left: 16px;">
        <form @submit.prevent="saveAIConfiguration">

            <div class="three-column-layout">
              <div class="column-group">
                <h4 class="column-header">Bedrock Configuration</h4>

                <label for="bedrock-api-key">Bedrock API Key</label>
                <div class="form-field">
                  <BaseInput
                    id="bedrock-api-key"
                    v-model="aiConfig.bedrock_api_key"
                    placeholder=""
                    type="password"
                    required />
                </div>

                <label for="bedrock-model">Bedrock Model</label>
                <div class="form-field">
                  <select
                    id="bedrock-model"
                    v-model="aiConfig.bedrock_model"
                    >
                    <option value="openai.gpt-oss-120b-1:0">OpenAI GPT OSS 120B</option>
                    <option value="meta.llama3-1-405b-instruct-v1:0">Llama 3.1 405B Instruct</option>
                  </select>
                </div>
              </div>

              <div class="column-group">
                <h4 class="column-header">Email Configuration</h4>

                <label for="alert-email">Alert Email</label>
                <div class="form-field">
                  <BaseInput
                    id="alert-email"
                    v-model="aiConfig.alert_email"
                    placeholder="email@example.com"
                    type="email" />
                  <small class="form-help-text">Email address to receive alerts when monitored webpages are updated</small>
                </div>

                <label for="from-email">From Email</label>
                <div class="form-field">
                  <BaseInput
                    id="from-email"
                    v-model="aiConfig.from_email"
                    placeholder="noreply@example.com"
                    type="email" />
                  <small class="form-help-text">Email address to use as sender for Genesys agentless email alerts</small>
                </div>
              </div>

              <div class="column-group">
                <h4 class="column-header">Genesys Configuration</h4>

                <label for="genesys-client-id">Genesys Client ID</label>
                <div class="form-field">
                  <BaseInput
                    id="genesys-client-id"
                    v-model="aiConfig.genesys_client_id"
                    placeholder=""
                    type="text"
                    required />
                </div>

                <label for="genesys-client-secret">Genesys Client Secret</label>
                <div class="form-field">
                  <BaseInput
                    id="genesys-client-secret"
                    v-model="aiConfig.genesys_client_secret"
                    placeholder=""
                    type="password"
                    required />
                </div>

                <label for="genesys-environment">Genesys Environment</label>
                <div class="form-field">
                  <select
                    id="genesys-environment"
                    v-model="aiConfig.genesys_environment"
                    >
                    <option value="usw2.pure.cloud">usw2.pure.cloud</option>
                    <option value="mypurecloud.com">mypurecloud.com</option>
                    <option value="usw2.pure.cloud">usw2.pure.cloud</option>
                    <option value="use2.us-gov-pure.cloud">use2.us-gov-pure.cloud</option>
                    <option value="cac1.pure.cloud">cac1.pure.cloud</option>
                    <option value="mypurecloud.ie">mypurecloud.ie</option>
                    <option value="euw2.pure.cloud">euw2.pure.cloud</option>
                    <option value="mypurecloud.de">mypurecloud.de</option>
                    <option value="aps1.pure.cloud">aps1.pure.cloud</option>
                    <option value="mypurecloud.jp">mypurecloud.jp</option>
                    <option value="apne2.pure.cloud">apne2.pure.cloud</option>
                    <option value="mypurecloud.com.au">mypurecloud.com.au</option>
                    <option value="sae1.pure.cloud">sae1.pure.cloud</option>
                  </select>
                </div>

                <label for="upload-directory">File Upload Directory</label>
                <div class="form-field">
                  <BaseInput
                    id="upload-directory"
                    v-model="aiConfig.upload_directory"
                    placeholder="/uploaded-files"
                    type="text"
                    required />
                  <small class="form-help-text">Directory path where uploaded files will be stored on scraperGCCX backend</small>
                </div>
              </div>
            </div>
            
            <div class="config-status" v-if="configStatus">
              <div :class="['status-message', configStatus.type]">
                {{ configStatus.message }}
              </div>
            </div>

            <div class="button-group" style="margin-top: 60px">
              <ButtonSubmit
                label="Save Configuration"
                :disabled="!hasChanges || savingConfig" />
                <ButtonSubmit
                label="Test Connection"
                :disabled="testingConfig"
                @click="testConfiguration" />

              <ButtonCancel
                label="Cancel"
                type="button"
                @click="cancelChanges" />
            </div>
          </form>
      </div>
    </div>
  </div>
</template>

<script>
import BaseInput from "./UI/BaseInput.vue"
import ButtonSubmit from "./ButtonSubmit.vue"
import ButtonCancel from "./ButtonCancel.vue"

export default {
  name: 'KnowledgeConfiguration',
  components: {
    BaseInput,
    ButtonSubmit,
    ButtonCancel
  },
  props: {
  },
  data: () => ({
    genesysStatus: { connected: false },
    aiConfig: {
        bedrock_api_key: '',
        bedrock_model: 'openai.gpt-oss-120b-1:0',
        alert_email: '',
        from_email: '',
        genesys_client_id: '',
        genesys_client_secret: '',
        genesys_environment: 'usw2.pure.cloud',
        upload_directory: '/uploaded-files'
    },
    originalConfig: null,
    savingConfig: false,
    testingConfig: false,
    configStatus: null,
  }),

  computed: {
    hasChanges() {
      if (!this.originalConfig) return false

      return Object.keys(this.aiConfig).some(key => {
        return this.aiConfig[key] !== this.originalConfig[key]
      })
    }
  },
  async mounted() {
    await this.loadAIConfiguration()
  },

  methods: {
    async loadAIConfiguration() {
        try {
            const company = localStorage.getItem("companyID") || "default"
            const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/ai-configuration?company=${company}`, {
            headers: {
                'Content-Type': 'application/json'
            }
            })
            
            if (response.ok) {
            const data = await response.json()
            if (data.success && data.configuration) {
                console.log('Loaded AI configuration from backend:', data.configuration)
                // Mask sensitive data for display
                this.aiConfig = {
                bedrock_api_key: data.configuration.bedrock_api_key ? '••••••••••••••••' : '',
                bedrock_model: data.configuration.bedrock_model || 'openai.gpt-oss-120b-1:0',
                alert_email: data.configuration.alert_email || '',
                from_email: data.configuration.from_email || '',
                genesys_client_id: data.configuration.genesys_client_id || '',
                genesys_client_secret: data.configuration.genesys_client_secret ? '••••••••••••••••' : '',
                genesys_environment: data.configuration.genesys_environment || 'usw2.pure.cloud',
                upload_directory: data.configuration.upload_directory || '/uploaded-files'
                }

                // Store original config for change detection
                this.originalConfig = { ...this.aiConfig }

                // Update Genesys connection status based on whether credentials exist
                const hasGenesysConfig = data.configuration.genesys_client_id && data.configuration.genesys_client_secret
                if (hasGenesysConfig) {
                // Test connection silently
                this.testGenesysConnection()
                } else {
                this.genesysStatus.connected = false
                }

                console.log('AI configuration loaded successfully')
            } else {
                console.log('No AI configuration found, using defaults')
                this.originalConfig = { ...this.aiConfig }
                this.genesysStatus.connected = false
            }
            } else {
            console.warn('Failed to load AI configuration:', response.status)
            this.genesysStatus.connected = false
            }
        } catch (error) {
            console.error('Failed to load AI configuration:', error)
            this.genesysStatus.connected = false
        }
    },
    async testGenesysConnection() {
    try {
        const company = localStorage.getItem("companyID") || "default"
        const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/test-ai-configuration?company=${company}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        }
        })
        
        const result = await response.json()
        
        if (result.success) {

        this.genesysStatus.connected = result.genesys_status === 'Connected'
        } else {
        this.genesysStatus.connected = false
        }
    } catch (error) {
        console.error('Silent Genesys connection test failed:', error)
        this.genesysStatus.connected = false
    }
    },
    cancelChanges() {
      // Reset to original configuration
      if (this.originalConfig) {
        this.aiConfig = { ...this.originalConfig }
      }
    },
    async testConfiguration() {
    this.testingConfig = true
    const company = localStorage.getItem("companyID")
    try {
        const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/test-ai-configuration?company=${company}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        }
        })
        
        const result = await response.json()
        
        if (result.success) {
        this.configStatus = {
            type: 'success',
            message: `Connection test successful! Bedrock: ${result.bedrock_status}, Genesys: ${result.genesys_status}`
        }
        } else {
        this.configStatus = {
            type: 'error',
            message: result.message || 'Connection test failed'
        }
        }
    } catch (error) {
        console.error('Configuration test failed:', error)
        this.configStatus = {
        type: 'error',
        message: 'Connection test failed'
        }
    } finally {
        this.testingConfig = false
    }
    },
    async saveAIConfiguration() {
      this.savingConfig = true
      const company = localStorage.getItem("companyID")
      try {
        // Only send non-masked values
        const configData = {}

        if (this.aiConfig.bedrock_api_key && !this.aiConfig.bedrock_api_key.includes('••••')) {
          configData.bedrock_api_key = this.aiConfig.bedrock_api_key
        }

        if (this.aiConfig.bedrock_model) {
          configData.bedrock_model = this.aiConfig.bedrock_model
        }

        if (this.aiConfig.alert_email) {
          configData.alert_email = this.aiConfig.alert_email
        }

        if (this.aiConfig.from_email) {
          configData.from_email = this.aiConfig.from_email
        }

        if (this.aiConfig.genesys_client_id) {
          configData.genesys_client_id = this.aiConfig.genesys_client_id
        }

        if (this.aiConfig.genesys_client_secret && !this.aiConfig.genesys_client_secret.includes('••••')) {
          configData.genesys_client_secret = this.aiConfig.genesys_client_secret
        }

        configData.genesys_environment = this.aiConfig.genesys_environment
        configData.upload_directory = this.aiConfig.upload_directory

        console.log('Saving AI configuration:', configData)

        const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/ai-configuration?company=${company}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(configData)
        })

        const result = await response.json()

        if (result.success) {
          this.configStatus = {
            type: 'success',
            message: 'Configuration saved successfully!'
          }

          // Update original config to reflect saved state
          this.originalConfig = { ...this.aiConfig }

          // Test Genesys connection after save
          await this.testGenesysConnection()
        } else {
          this.configStatus = {
            type: 'error',
            message: result.message || 'Failed to save configuration'
          }
        }
      } catch (error) {
        console.error('Failed to save AI configuration:', error)
        this.configStatus = {
          type: 'error',
          message: 'Failed to save configuration'
        }
      } finally {
        this.savingConfig = false
      }
    }
  },
}
</script>
<style scoped>
header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}
.genesys-status {
  display: flex;
  align-items: center;
  gap: 8px;
}
.status-indicator {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-indicator.connected {
  background: #dcfce7;
  color: #166534;
}

.status-indicator.disconnected {
  background: #fef2f2;
  color: #dc2626;
}

.three-column-layout {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-top: 20px;
}

.column-group {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
}

.column-header {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #d1d5db;
  padding-bottom: 8px;
}

.column-group label {
  font-weight: 500;
  color: #374151;
  font-size: 14px;
  margin-bottom: 4px;
}

.column-group .form-field {
  margin-bottom: 8px;
}

.button-group {
  display: flex;
  gap: 12px;
  justify-content: start;
  flex-wrap: wrap;
}

.status-message {
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
}

.status-message.success {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.status-message.error {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}
.form-control {
  display: block;
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background-color: #ffffff;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-control-textarea {
  display: block;
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-family: inherit;
  resize: vertical;
  font-size: 16px;
  background-color: #ffffff;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.form-control-textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  line-height: 1.4;
}

select {
  font-weight: 400;
  /*  margin-bottom: 20px;*/
  font-size: 1rem;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  color: rgba(0, 0, 0, 0.87);
  box-sizing: border-box;
  cursor: text;
  display: inline-flex;
  -webkit-box-align: center;
  align-items: center;
  width: 100%;
  position: relative;
  border-radius: 4px;
  /*  padding: 16.5px 14px;*/
  padding: 8px 5px;
  box-shadow: 2px 4px #ccc;
  border: 2px solid #000000;
}
.form-help-text {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
}

@media (max-width: 1200px) {
  .three-column-layout {
    grid-template-columns: 1fr;
  }
}

</style>